// Chapter CRUD wrapper around ``/api/chapters/<id>/`` and
// ``/api/projects/<id>/chapters/``. Lets WriteProject load an editor via a
// direct chapter-id lookup instead of walking every project + every chapter
// until a matching ``docname`` turns up (the old Firestore shape).

import { apiClient } from './apiClient';

export async function getChapter(chapterId) {
  const { data } = await apiClient.get(`/api/chapters/${chapterId}/`);
  return data;
}

export async function updateChapter(chapterId, patch) {
  const { data } = await apiClient.patch(`/api/chapters/${chapterId}/`, patch, {
    headers: { 'Content-Type': 'application/json' },
  });
  // Tier-6 §17 follow-up — emit a window event so the planner's
  // appears-in-chapters map can refetch. Single emit point covers
  // every save path (autosave, manual save, version restore writes
  // through here). Listeners debounce so autosave bursts coalesce.
  // Best-effort: a missing window (SSR, tests) silently no-ops.
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(
        new CustomEvent('wo:chapter-saved', {
          detail: {
            chapterId: data?.id ?? chapterId,
            projectId: data?.project_id ?? null,
          },
        }),
      );
    }
  } catch {
    // CustomEvent unsupported, or window mid-teardown — drop silently.
  }
  return data;
}

export async function deleteChapter(chapterId) {
  await apiClient.delete(`/api/chapters/${chapterId}/`);
}

export async function listProjectChapters(projectId) {
  const { data } = await apiClient.get(`/api/projects/${projectId}/chapters/`);
  return data?.chapters ?? [];
}

export async function getProject(projectId) {
  const { data } = await apiClient.get(`/api/projects/${projectId}/`);
  return data;
}

// Versioning — auto-snapshots taken on every save (debounced 5min)
// plus explicit named ones. List is owner-or-collaborator-readable;
// restore is owner-only and the backend stamps a pre-restore
// snapshot so the rollback itself is reversible.

export async function listChapterVersions(chapterId) {
  const { data } = await apiClient.get(
    `/api/chapters/${chapterId}/versions/`,
  );
  return data?.versions ?? [];
}

export async function getChapterVersion(chapterId, versionId) {
  const { data } = await apiClient.get(
    `/api/chapters/${chapterId}/versions/${versionId}/`,
  );
  return data;
}

export async function saveChapterVersion(chapterId, label = "") {
  const { data } = await apiClient.post(
    `/api/chapters/${chapterId}/versions/`,
    { label },
    { headers: { "Content-Type": "application/json" } },
  );
  return data;
}

export async function restoreChapterVersion(chapterId, versionId) {
  const { data } = await apiClient.post(
    `/api/chapters/${chapterId}/versions/${versionId}/restore/`,
    {},
    { headers: { "Content-Type": "application/json" } },
  );
  return data;
}

/**
 * Fallback resolver for legacy ``/editor/<base64 docname>`` links. Walks the
 * user's projects until it finds a matching docname. Returns
 * ``{ project, chapter, chapters, index }`` or ``null``.
 *
 * Prefer a direct ``getChapter(id)`` whenever the numeric chapter id is
 * known — this walk is O(projects × chapters) and only exists to keep old
 * bookmarks alive.
 */
export async function resolveChapterByDocname(docname) {
  if (!docname) return null;
  const { data: projectsResp } = await apiClient.get('/api/projects/');
  const projects = projectsResp?.projects ?? [];
  for (const project of projects) {
    const chapters = await listProjectChapters(project.id);
    const index = chapters.findIndex((c) => c.docname === docname);
    if (index !== -1) {
      return { project, chapter: chapters[index], chapters, index };
    }
  }
  return null;
}
